import type { TPapelComprador } from '@/features/proposta/interfaces/proposta.type'
import type {
  CompradorPessoaFisicaStepSchemaOutput,
  CompradorVinculoStepSchemaOutput,
} from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import type { CompradorPessoaJuridicaSchemaOutput } from '@/features/proposta/schemas/compradores/comprador-pj.schema'

export interface ApiCommand<TInput> {
  executar: () => Promise<TInput>
}

export interface CompradorFisicoService {
  create: (
    data:
      | CompradorPessoaFisicaStepSchemaOutput
      | CompradorPessoaJuridicaSchemaOutput
      | CompradorVinculoStepSchemaOutput,
    tipo: TPapelComprador,
  ) => Promise<{ id: string }>
  update: (
    data:
      | CompradorPessoaFisicaStepSchemaOutput
      | CompradorPessoaJuridicaSchemaOutput
      | CompradorVinculoStepSchemaOutput,
    tipo: TPapelComprador,
  ) => Promise<unknown>
}
export interface CompradorJuridicoService {
  create: (data: CompradorPessoaJuridicaSchemaOutput) => Promise<{ id: string }>
  update: (data: CompradorPessoaJuridicaSchemaOutput) => Promise<void>
}

class SincronizarCompradorCommand implements ApiCommand<CompradorPessoaFisicaStepSchemaOutput> {
  constructor(
    private readonly comprador: CompradorPessoaFisicaStepSchemaOutput,
    private readonly isDirty: boolean,
    private readonly service: CompradorFisicoService,
  ) {}

  async executar(): Promise<CompradorPessoaFisicaStepSchemaOutput> {
    const temId = !!this.comprador.dados.id
    if (temId) {
      if (this.isDirty) {
        await this.service.update(this.comprador, 'Comprador')
      }
      return this.comprador
    }
    const { id } = await this.service.create(this.comprador, 'Comprador')
    this.comprador.dados.id = id
    return this.comprador
  }
}

class SincronizarVinculoIndependente implements ApiCommand<CompradorVinculoStepSchemaOutput> {
  constructor(
    private readonly vinculo: CompradorVinculoStepSchemaOutput,
    private readonly isDirty: boolean,
    private readonly service: CompradorFisicoService,
  ) {}

  async executar(): Promise<CompradorVinculoStepSchemaOutput> {
    const clienteJaCadastrado = !!this.vinculo.dados.id
    if (clienteJaCadastrado) {
      if (this.isDirty) {
        await this.service.update(this.vinculo, 'Comprador')
      }
      return this.vinculo
    }

    const { id } = await this.service.create(this.vinculo, this.vinculo.tipo)
    this.vinculo.dados.id = id
    return this.vinculo
  }
}

class SincronizarConjugeCommand implements ApiCommand<CompradorPessoaFisicaStepSchemaOutput> {
  constructor(
    private readonly titular: CompradorPessoaFisicaStepSchemaOutput,
    private readonly isDirty: boolean,
    private readonly service: CompradorFisicoService,
  ) {}

  async executar(): Promise<CompradorPessoaFisicaStepSchemaOutput> {
    if (this.titular.tipo !== 'PessoaFisica') return this.titular
    if (!this.titular.conjuge) return this.titular

    const clienteJaCadastrado = !!this.titular.conjuge.dados.id
    if (clienteJaCadastrado) {
      if (this.isDirty) {
        await this.service.update(this.titular, 'Conjuge')
      }
      return this.titular
    }

    const { id } = await this.service.create(this.titular, 'Conjuge')
    this.titular.conjuge.dados.id = id
    return this.titular
  }
}

class SincronizarMaePaiCommand implements ApiCommand<CompradorPessoaFisicaStepSchemaOutput> {
  constructor(
    private readonly titular: CompradorPessoaFisicaStepSchemaOutput,
    private readonly isDirty: boolean,
    private readonly service: CompradorFisicoService,
  ) {}

  async executar(): Promise<CompradorPessoaFisicaStepSchemaOutput> {
    if (this.titular.tipo !== 'PessoaFisica') return this.titular
    if (!this.titular.maePai) return this.titular

    const clienteJaCadastrado = !!this.titular.maePai.dados.id
    if (clienteJaCadastrado) {
      if (this.isDirty) {
        await this.service.update(this.titular, 'MaePai')
      }
      return this.titular
    }

    const { id } = await this.service.create(this.titular, 'MaePai')
    this.titular.maePai.dados.id = id
    return this.titular
  }
}

class SincronizarPessoaJuridicaCommand implements ApiCommand<CompradorPessoaJuridicaSchemaOutput> {
  constructor(
    private readonly comprador: CompradorPessoaJuridicaSchemaOutput,
    private readonly isDirty: boolean,
    private readonly service: CompradorJuridicoService,
  ) {}

  async executar(): Promise<CompradorPessoaJuridicaSchemaOutput> {
    const temId = !!this.comprador.dados.id
    if (temId) {
      if (!this.isDirty) {
        return this.comprador
      }
      await this.service.update(this.comprador)
      return this.comprador
    }

    const { id } = await this.service.create(this.comprador)
    this.comprador.dados.id = id
    return this.comprador
  }
}

class SincronizarResponsavelCommand implements ApiCommand<CompradorPessoaJuridicaSchemaOutput> {
  constructor(
    private readonly comprador: CompradorPessoaJuridicaSchemaOutput,
    private readonly isDirty: boolean,
    private readonly service: CompradorFisicoService,
  ) {}

  async executar(): Promise<CompradorPessoaJuridicaSchemaOutput> {
    if (!this.comprador.responsavel) return this.comprador

    const clienteJaCadastrado = !!this.comprador.responsavel.dados.id
    if (clienteJaCadastrado) {
      if (this.isDirty) {
        await this.service.update(this.comprador, this.comprador.responsavel.tipo)
      }
      return this.comprador
    }
    const { id } = await this.service.create(this.comprador, this.comprador.responsavel.tipo)
    this.comprador.responsavel.dados.id = id
    return this.comprador
  }
}

class CompositeCommand<T> implements ApiCommand<T> {
  constructor(
    private readonly comandos: Array<ApiCommand<unknown>>,
    private readonly root: T,
  ) {}

  async executar(): Promise<T> {
    for (const cmd of this.comandos) {
      await cmd.executar()
    }
    return this.root
  }
}

type ServicosAPI = {
  fisico: CompradorFisicoService
  juridico: CompradorJuridicoService
}

type TApiCommandFactory =
  | CompradorPessoaFisicaStepSchemaOutput
  | CompradorPessoaJuridicaSchemaOutput
  | CompradorVinculoStepSchemaOutput

export class ApiCommandFactory {
  static criar(comprador: TApiCommandFactory, isDirty: boolean, servicos: ServicosAPI): ApiCommand<TApiCommandFactory> {
    if (comprador.tipo === 'PessoaFisica') return this.sincronizarPessoaFisicaCommand(comprador, isDirty, servicos)
    if (comprador.tipo === 'PessoaJuridica') return this.sincronizarPessoaJuridicaCommand(comprador, isDirty, servicos)
    return new SincronizarVinculoIndependente(comprador, isDirty, servicos.fisico)
  }

  private static sincronizarPessoaFisicaCommand(
    comprador: CompradorPessoaFisicaStepSchemaOutput,
    isDirty: boolean,
    servicos: ServicosAPI,
  ): ApiCommand<CompradorPessoaFisicaStepSchemaOutput> {
    const edicaoFluxoIndependente = comprador.fluxoIndependente && comprador.mode === 'edit'

    if (edicaoFluxoIndependente) {
      if (comprador.conjuge) {
        return new CompositeCommand([new SincronizarConjugeCommand(comprador, isDirty, servicos.fisico)], comprador)
      }

      if (comprador.maePai) {
        return new CompositeCommand([new SincronizarMaePaiCommand(comprador, isDirty, servicos.fisico)], comprador)
      }
    }

    return new CompositeCommand<CompradorPessoaFisicaStepSchemaOutput>(
      [
        new SincronizarCompradorCommand(comprador, isDirty, servicos.fisico),
        ...(comprador.conjuge ? [new SincronizarConjugeCommand(comprador, isDirty, servicos.fisico)] : []),
        ...(comprador.maePai ? [new SincronizarMaePaiCommand(comprador, isDirty, servicos.fisico)] : []),
      ],
      comprador,
    )
  }

  private static sincronizarPessoaJuridicaCommand(
    comprador: CompradorPessoaJuridicaSchemaOutput,
    isDirty: boolean,
    servicos: ServicosAPI,
  ): ApiCommand<CompradorPessoaJuridicaSchemaOutput> {
    if (comprador.fluxoIndependente && comprador.mode === 'edit') {
      return new CompositeCommand<CompradorPessoaJuridicaSchemaOutput>(
        [new SincronizarResponsavelCommand(comprador, isDirty, servicos.fisico)],
        comprador,
      )
    }

    return new CompositeCommand<CompradorPessoaJuridicaSchemaOutput>(
      [
        new SincronizarPessoaJuridicaCommand(comprador, isDirty, servicos.juridico),
        new SincronizarResponsavelCommand(comprador, isDirty, servicos.fisico),
      ],
      comprador,
    )
  }
}
